import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { ApiClient } from "../api/client";
import type { OperationId } from "../api/operations";
import type { EventResource, MixOutputTrack } from "../api/types";
import { CommandTracker } from "../state/commands";
import { ToastStore } from "../state/toasts";
import { SavedIndex } from "../state/saved";
import { SessionStore } from "../state/session";
import { createStore, useStore, type Store } from "../state/store";
import { PlaybackSync, mixSourceRate, positionAt, type SyncState } from "../state/sync";
import { controlGate, type Gate, type GateLevel } from "../lib/gates";

export interface Services {
  api: ApiClient;
  session: SessionStore;
  sync: PlaybackSync;
  commands: CommandTracker;
  toasts: ToastStore;
  /** どれを保存しているか */
  saved: SavedIndex;
  /** 投稿・ライブラリなどの「変わった」通知の回数。画面はこれを見て取り直す */
  invalidations: Store<Record<EventResource, number>>;
}

export function createServices(api: ApiClient, opts: { pollMs?: number } = {}): Services {
  const session = new SessionStore(api);
  const sync = new PlaybackSync(api, { pollMs: opts.pollMs });
  const commands = new CommandTracker(api, sync);
  const saved = new SavedIndex(api);
  const toasts = new ToastStore();
  session.store.subscribe(() => {
    if (session.store.get().status !== "signed_in") {
      commands.clear();
      toasts.clear();
    }
  });
  const invalidations = createStore<Record<EventResource, number>>({ playback: 0, queue: 0, commands: 0, posts: 0, library: 0, me: 0 });
  // 通知は数秒おきに来ることがある（contracts/README.md：いまは2秒間隔の粗い通知）。/me の取り直しは15秒に1回まで
  let lastMe = 0;
  const refreshMe = (force = false) => {
    const now = Date.now();
    if (!force && now - lastMe < 15_000) return;
    lastMe = now;
    void session.load();
  };
  // 画面の取り直しは、続けて来た通知をまとめて（0.6秒）、投稿・ライブラリは5秒に1回までにする
  const pending = new Set<EventResource>();
  const lastBump: Partial<Record<EventResource, number>> = {};
  let timer: ReturnType<typeof setTimeout> | null = null;
  const flush = () => {
    timer = null;
    const now = Date.now();
    const due = [...pending].filter((r) => r === "playback" || r === "queue" || r === "commands" || now - (lastBump[r] ?? 0) >= 5_000);
    due.forEach((r) => {
      pending.delete(r);
      lastBump[r] = now;
    });
    if (due.length) {
      invalidations.set((st) => {
        const next = { ...st };
        due.forEach((r) => (next[r] += 1));
        return next;
      });
      if (due.includes("library")) void saved.load();
    }
    if (pending.size) timer = setTimeout(flush, 1_000);
  };
  sync.onEvent((event) => {
    const all: EventResource[] = ["playback", "queue", "commands", "posts", "library", "me"];
    const touched = event.type === "access_revoked" ? all : event.resources;
    if (event.type === "access_revoked") touched.forEach((r) => (lastBump[r] = 0));
    touched.forEach((r) => pending.add(r));
    if (!timer) timer = setTimeout(flush, 600);
    // 本人のVC・権限変更は me 通知で即時再取得。旧通知の補完は15秒に1回。
    // 権限が取り消された：いったん接続と保護表示を閉じ（signed_in でなくなると AppShell が同期を止める）、/me で確かめ直す
    if (event.type === "access_revoked") void session.revoke();
    else if (event.resources.includes("me")) refreshMe(true);
    else if (event.resources.includes("playback")) refreshMe();
  });
  // つなぎ直したら /me も取り直す（contracts/README.md：再接続のたび /me /playback /queue）
  sync.onConnected(() => refreshMe(true));
  return { api, session, sync, commands, toasts, saved, invalidations };
}

const ServicesContext = createContext<Services | null>(null);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

export function useServices(): Services {
  const s = useContext(ServicesContext);
  if (!s) throw new Error("ServicesProvider is missing");
  return s;
}

export function useApi(): ApiClient {
  return useServices().api;
}

export function useConnected(op: OperationId): boolean {
  return useServices().api.isConnected(op);
}

export function useSession() {
  return useStore(useServices().session.store);
}

export function useMe() {
  const s = useSession();
  return s.status === "signed_in" ? s.me : null;
}

export function useSync(): SyncState {
  return useStore(useServices().sync.store);
}

export function useCommands() {
  return useStore(useServices().commands.store);
}

export function useInvalidation(resources: EventResource[]): number {
  const counts = useStore(useServices().invalidations);
  return resources.reduce((n, r) => n + counts[r], 0);
}

/** 画面の時計。再生位置を進めるときだけ1秒ごとに進む */
export function useNow(active: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [active, intervalMs]);
  return now;
}

/** 画面に出す再生位置（更新停止中は止まる） */
export function usePosition(mixTrack?: MixOutputTrack): number | null {
  const sync = useSync();
  const mix = sync.playback?.mix;
  const ticking = sync.conn === "live" && (mixTrack
    ? !!mix && ["active", "draining"].includes(mix.phase) && mix.pause_reasons.length === 0 &&
      mix.output?.status === "confirmed" && mixSourceRate(mixTrack) !== null
    : sync.playback?.state === "playing");
  const now = useNow(ticking);
  return positionAt(sync, now, mixTrack);
}

export function useGate(level: GateLevel): Gate {
  const { api } = useServices();
  const me = useMe();
  const sync = useSync();
  return controlGate(level, {
    connected: api.isConnected("commands.create"),
    me,
    conn: sync.conn,
    playback: sync.playback,
  });
}
